/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NEGATIVE_COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";
import { getChartResolution, readStoredResolution, storeResolution } from "../../constants/chart/resolutions";
import { reconcileReadyResolution } from "./readyResolution";
import { areDevOverridesAllowed } from "../../config/features/devOverrides";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { SettingsContext } from "../../contexts/SettingsContext";
import { fetchCandles } from "../../hooks/api/candles/useCandles";
import useCandlesWSS from "../../hooks/wss/useCandlesWSS";
import { getDisplayInstrumentName } from "../../utils/displayName";
import type { ITradingViewChartProps } from "./LightweightCandles";
import NativeCandlesChart from "./NativeCandlesChart";
import { createOwnDatafeed, OwnMarketInfo, seriesKey, TvBar } from "./ownDatafeed";
import { Container } from "./style";
import { drawTradingLines } from "./tvLines";
import { LIBRARY_PATH, loadChartingLibrary } from "./tvLibraryLoader";
import { translateNaToZero } from "./translateNaToZero";
import { browserTvTimezone } from "./tvTimezone";
import { buildOverrides, buildStudiesOverrides, CHART_BG, themeLineColors } from "./tvOverrides";
import useSaveloadAdapter from "./useSaveloadAdapter";

/**
 * TradingView Advanced Charts (the licensed Charting Library, self-hosted
 * under /charting_library/) drawing OUR fills: history from GET /candles and
 * live bars from the `candles` channel, through `createOwnDatafeed`. The full
 * TradingView UI — drawing tools, indicators, chart types, layouts — over this
 * exchange's own book; never another venue's prices.
 *
 * Resolutions use the library's own interval menu, limited to the backend's
 * set: the datafeed advertises only CHART_RESOLUTIONS, and custom intervals and
 * the type-a-digit interval dialog are disabled, so no timeframe the backend
 * rejects (2h, 3m, …) can be chosen.
 *
 * If the library bundle is not deployed, it falls back to NativeCandlesChart,
 * which draws the same bars with lightweight-charts.
 */
function TvLibraryChart(props: ITradingViewChartProps) {
  const { symbol, isMobileScreen, hidden, load = true, priceDecimals = 2, amountDecimals = 4, position, orders, onCancelOrder } = props;
  const { directionScheme, customDirection } = useContext(SettingsContext);
  const { activePerpMarkets, activeSpotMarkets } = useContext(MarketInstrumentContext);
  const { adapter } = useSaveloadAdapter();
  const [resolution, setResolution] = useState<string>(readStoredResolution);
  const [available, setAvailable] = useState<boolean | undefined>();
  const [ready, setReady] = useState(false);
  const widgetCtorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);

  // Read through a ref so the datafeed (built once) always sees current data.
  const marketRef = useRef<(s: string) => OwnMarketInfo | undefined>(() => undefined);
  marketRef.current = (s: string) => {
    const known = [...(activePerpMarkets ?? []), ...(activeSpotMarkets ?? [])].some((m) => m.instrument_name === s);
    if (!known && s !== symbol) return undefined;
    return {
      name: s,
      displayName: getDisplayInstrumentName(s) || s,
      priceDecimals,
      amountDecimals,
      exchange: process.env.REACT_APP_BRAND_SHORT_NAME || "",
    };
  };

  // The series on screen, and each series' first-load outcome.
  const currentKeyRef = useRef(seriesKey(symbol, resolution));
  currentKeyRef.current = seriesKey(symbol, resolution);
  const resolutionRef = useRef(resolution);
  resolutionRef.current = resolution;
  const [seriesStatus, setSeriesStatus] = useState<Record<string, "empty" | "loaded" | "error">>({});
  const publishRef = useRef<() => void>(() => {});

  const datafeed = useMemo(() => {
    const drawn = new Map<string, Map<number, TvBar>>();
    // QA handle, same shape as NativeCandlesChart's: the bars the library was
    // given for the series ON SCREEN. Only that series — a poll from the
    // previous resolution can land after a switch and must not replace it.
    const publish = () => {
      const series = drawn.get(currentKeyRef.current);
      (window as any).__nativeChartBars = [...(series?.values() ?? [])]
        .sort((a, b) => a.time - b.time)
        .map((b) => ({ ...b, time: b.time / 1000 }));
      (window as any).__nativeChartSeries = currentKeyRef.current;
    };
    publishRef.current = publish;
    return createOwnDatafeed({
      getMarket: (s) => marketRef.current(s),
      fetchBars: fetchCandles,
      onDelivered: (key, bars) => {
        if (!areDevOverridesAllowed()) return;
        const series = drawn.get(key) ?? new Map<number, TvBar>();
        bars.forEach((b) => series.set(b.time, b));
        drawn.set(key, series);
        publish();
      },
      onSeriesStatus: (key, status) => {
        setSeriesStatus((prev) => (prev[key] === status ? prev : { ...prev, [key]: status }));
        if (areDevOverridesAllowed()) publish();
      },
    });
  }, []);

  // Same messages as the native chart when there is nothing to draw.
  const status = (() => {
    const s = seriesStatus[seriesKey(symbol, resolution)];
    if (s === "empty") return "No trades yet";
    if (s === "error") return "Chart data unavailable";
    return null;
  })();

  useEffect(() => () => datafeed.dispose(), [datafeed]);

  // Revisiting a resolution is served from the library's own cache with no
  // getBars call, so republish the QA handle whenever the visible series changes.
  useEffect(() => {
    if (areDevOverridesAllowed()) publishRef.current();
  }, [symbol, resolution]);

  useEffect(() => {
    let cancelled = false;
    loadChartingLibrary()
      .then((ctor) => {
        widgetCtorRef.current = ctor;
        if (!cancelled) setAvailable(true);
      })
      .catch(() => !cancelled && setAvailable(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Build the widget once per symbol (callers remount per market anyway).
  useEffect(() => {
    if (!load || !available || !containerRef.current || !widgetCtorRef.current) return undefined;
    setReady(false);
    let tv: any;
    try {
      tv = new widgetCtorRef.current({
        container: containerRef.current,
        library_path: LIBRARY_PATH,
        datafeed,
        symbol,
        interval: resolution,
        locale: "en",
        timezone: browserTvTimezone(),
        autosize: true,
        theme: "Dark",
        toolbar_bg: CHART_BG,
        loading_screen: { backgroundColor: CHART_BG, foregroundColor: "#4CD7F4" },
        overrides: buildOverrides(),
        studies_overrides: buildStudiesOverrides(),
        save_load_adapter: adapter,
        // Display only: cursor off a bar still has no candle (has_empty_bars
        // stays false). The library legend would print t("n/a"); show Ø instead.
        custom_translate_function: translateNaToZero,
        disabled_features: [
          "header_symbol_search",
          "symbol_search_hot_key",
          "header_compare",
          "timeframes_toolbar",
          // Keep the interval menu to the datafeed's supported set: no custom
          // intervals, and no free-text interval dialog (a subset of
          // header_resolutions that would otherwise come back with it).
          "custom_resolutions",
          "header_interval_dialog_button",
          "show_interval_dialog_on_key_press",
          "popup_hints",
          ...(isMobileScreen ? ["left_toolbar", "header_saveload"] : []),
        ],
        enabled_features: [
          "study_templates",
          // Draw D/W/M bars at the times we serve. Without it the library
          // re-aligns them to its session's first day of week (Monday for
          // 24x7), drawing every Sunday-bucketed week one week early. Safe:
          // has_empty_bars is false and every resolution is served natively.
          "disable_resolution_rebuild",
        ],
      });
    } catch {
      // Never let the chart take the trading page down: draw the same bars
      // with the native chart instead.
      setAvailable(false);
      return undefined;
    }
    widgetRef.current = tv;
    tv.onChartReady(() => {
      setReady(true);
      if (areDevOverridesAllowed()) {
        // QA handle: bar times (unix s) the LIBRARY holds for the series on
        // screen, after any rewrite of its own. __nativeChartBars only shows
        // what the datafeed handed it.
        (window as any).__tvChartBarTimes = async () => {
          const { schema, data } = await tv
            .activeChart()
            .exportData({ includeTime: true, includeSeries: true, includedStudies: [] });
          const col = schema.findIndex((f: { type?: string }) => f.type === "time");
          // Never read another column as time: a missing column is an error.
          if (col < 0) throw new Error("__tvChartBarTimes: exportData returned no time column");
          return data.map((row: ArrayLike<number>) => row[col]);
        };
      }
      // The interval menu is live before this callback; a pick made in that
      // window reached the chart but not React. Keep the user's choice.
      const next = reconcileReadyResolution(tv.activeChart().resolution(), resolutionRef.current);
      if (next && "adopt" in next) {
        setResolution(next.adopt);
        storeResolution(next.adopt);
      } else if (next) {
        tv.activeChart().setResolution(next.push);
      }
      tv.activeChart()
        .onIntervalChanged()
        .subscribe(null, (interval: string) => {
          // The built-in menu only offers the datafeed's resolutions, but never
          // store or subscribe to one the backend would reject.
          if (!getChartResolution(interval)) return;
          setResolution(interval);
          storeResolution(interval);
        });
    });
    return () => {
      setReady(false);
      try {
        tv.remove();
      } catch {
        /* already gone */
      }
      // The datafeed outlives this widget (a same-key symbol switch rebuilds
      // without unmounting, as on mobile), and remove() is not guaranteed to
      // unsubscribe — stop the old series' polls; the next widget resubscribes.
      datafeed.dispose();
      widgetRef.current = null;
      delete (window as any).__tvChartBarTimes;
    };
    // resolution changes arrive through onIntervalChanged, not by rebuilding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, load, symbol, datafeed, adapter, isMobileScreen]);

  // Live bars from the websocket channel.
  const onLiveBar = useCallback((bar: any) => datafeed.pushLiveBar(symbol, resolution, bar), [datafeed, symbol, resolution]);
  useCandlesWSS(load && available ? symbol : undefined, resolution, onLiveBar);

  // Follow the user's colour scheme without rebuilding the chart.
  useEffect(() => {
    if (!ready) return;
    widgetRef.current?.applyOverrides(buildOverrides());
  }, [ready, directionScheme, customDirection]);

  // Position, liquidation and order lines.
  useEffect(() => {
    if (!ready || !widgetRef.current) return undefined;
    return drawTradingLines(widgetRef.current.activeChart(), {
      position,
      orders,
      colors: themeLineColors(),
      priceDecimals,
      onCancelOrder,
    });
  }, [ready, position, orders, onCancelOrder, priceDecimals, directionScheme, customDirection]);

  if (available === false) return <NativeCandlesChart {...props} />;

  return (
    <Container
      data-testid="native-candles-chart"
      data-chart-engine="tradingview-library"
      data-series={seriesKey(symbol, resolution)}
      style={{
        height: isMobileScreen ? `${COMPONENTS.mobileTvChart}px` : "100%",
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {status && (
        <div
          data-testid="chart-series-status"
          style={{
            // Overlaid, not a row: a row that comes and goes would resize the chart.
            // Bottom-centre, above the time axis: the library draws its own centred
            // "No data here" placeholder on an empty series, and this must not sit on it.
            position: "absolute",
            bottom: SPACING.five + SPACING.two,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
            pointerEvents: "none",
            padding: `${SPACING.one}px ${SPACING.two}px`,
            fontSize: FONT_SIZE.one,
            // DESIGN.md: errors inline in the negative colour; an empty market is muted text.
            color: seriesStatus[seriesKey(symbol, resolution)] === "error" ? NEGATIVE_COLORS.one : TEXT_COLORS.three,
          }}
        >
          {status}
        </div>
      )}
      <div ref={containerRef} data-testid="native-candles-canvas" style={{ flex: "1 1 auto", minHeight: 0 }} />
    </Container>
  );
}

export default TvLibraryChart;
